import { useTranslation } from 'react-i18next'

import { getFileItems } from '../../../../../api'
import { EMPTY_FUNCTION } from '../../../../../constants/common'
import { REQUEST_PROFILE_CHANGE_TYPE } from '../../../../../constants/request'
import { useNotification, useTimeZone, useUk } from '../../../../../hooks'
import { FileAttachItemsModal } from '../../../../File'
import {
  getPipelineActivity,
  updatePipelineActivity,
} from '../../../../Pipeline'
import {
  getTaskFormEditPayload,
  getTaskFormInitialValues,
  getTaskNextFileIds,
  getTaskUpdateErrorNotificationKey,
  TASK_ACTION_ATTACH_EMAILS,
  TASK_ACTION_ATTACH_FILE,
  TASK_ACTION_MARK_AS_DONE,
  TASK_ACTION_MARK_AS_HIDDEN,
  TASK_ACTION_MARK_AS_OPEN,
  TASK_ACTION_MARK_AS_SHARED,
  TASK_ACTION_SEND_TO_DWELLER,
  TASK_ACTION_SEND_TO_PROVIDER,
  TASK_REQUEST_FIELDS,
  TaskFormModal,
  updateTask,
  useTaskSendEmailTableAction,
  useTaskTableAttachEmailThreads,
  useTaskTableItemAttachedEmailThreads,
  useTaskTableItemAttachedFiles,
  useTaskTableItemAttachFile,
} from '../../../../Task'
import { REQUEST_PROFILE_TAB_EMAILS } from '../../../RequestProfile.constants'
import { useRequestProfileContext } from '../../../RequestProfile.hooks'
import {
  getRequestEmailAttachProps,
  getRequestTemplateListApiParams,
  getStandardFilterModalInitialValues,
  RequestProfileTabEmailsThreadModal,
} from '../../../RequestProfileTabEmails'
import {
  ACTIVITY_ACTION_ATTACH_FROM_DOCUMENT_STORAGE,
  ACTIVITY_ACTION_ATTACH_FROM_REQUEST_FOLDER,
  ACTIVITY_ACTION_EDIT,
} from '../../RequestProfileTabActivities.constants'
import {
  getActivityEditSuccessNotificationProps,
  getActivityStatusChangeNotificationKey,
  getActivityVisibilityChangeNotificationKey,
} from '../../RequestProfileTabActivities.utils'
import { useRequestProfileTabActivitiesContext } from '../useRequestProfileTabActivitiesContext'
import { getPhaseIdsToReload } from '../useRequestProfileTabActivitiesLoader'

export function useRequestProfileTabActivitiesTableActions() {
  const {
    closeEmailFormModal,
    closeModal,
    handleReload,
    handleReloadAll,
    handleReloadAllDelayed,
    setEmailFormModal,
    setModal,
  } = useRequestProfileTabActivitiesContext()

  const {
    handleAddErrorNotification,
    handleAddNotification,
    handleAddSuccessNotification,
  } = useNotification()

  const { t } = useTranslation()

  const timezone = useTimeZone()

  const {
    handleLoadCounters,
    handleReloadAfterChange,
    handleReloadOpenActivities,
    outboundUuid,
    requestData,
  } = useRequestProfileContext()

  const { directory_id: ukDocumentsFolderId } = useUk()

  function handleReloadAfterActivityChange(activity, previousPhaseId) {
    handleReloadOpenActivities?.()

    return handleReload({
      phaseIds: getPhaseIdsToReload({
        phaseId: activity?.phase?.id,
        previousPhaseId,
      }),
    })
  }

  // Status changes can fire pipeline automations (e.g. start the next phase),
  // so activities with automations reload every phase, not only the changed one,
  // and once more later because the automations finish after the response.
  function handleReloadAfterStatusChange(activity, updatedActivity) {
    if (activity.active_automation_count > 0) {
      handleReloadAllDelayed()

      return handleReloadAll()
    }

    return handleReloadAfterActivityChange(updatedActivity, activity.phase?.id)
  }

  async function handleChangeStatus({ activity, data, id }) {
    return updateTask(data, id)
      .then(response => {
        handleAddSuccessNotification(
          getActivityStatusChangeNotificationKey(data)
        )

        return handleReloadAfterStatusChange(
          activity,
          response?.data || activity
        )
      })
      .catch(error => {
        handleAddErrorNotification(
          getTaskUpdateErrorNotificationKey(
            error,
            getActivityStatusChangeNotificationKey(data, { isError: true })
          )
        )
      })
  }

  async function handleChangeVisibility({ activity, data, id }) {
    const previousPhaseId = activity?.phase?.id
    const isVisible = data.visible_to_tenant

    return updateTask(data, id)
      .then(response => {
        handleAddSuccessNotification(
          getActivityVisibilityChangeNotificationKey(isVisible)
        )

        return handleReloadAfterActivityChange(
          response?.data || activity,
          previousPhaseId
        )
      })
      .catch(() => {
        handleAddErrorNotification(
          getActivityVisibilityChangeNotificationKey(isVisible, {
            isError: true,
          })
        )
      })
  }

  const { handleAttachEmails } = useTaskTableAttachEmailThreads({
    setModal,
    onAddErrorNotification: handleAddErrorNotification,
    onAddSuccessNotification: handleAddSuccessNotification,
    onCloseModal: closeModal,
    onReload: handleReloadAll,
  })

  function handleAttachedThreadRead() {
    handleLoadCounters?.([REQUEST_PROFILE_TAB_EMAILS])
  }

  function renderThreadModal({ navigationContext, uuid, onClose, onReload }) {
    async function handleReloadAttachedThreadChanges() {
      onReload()

      return handleReloadAfterChange(REQUEST_PROFILE_CHANGE_TYPE.emails)
    }

    return (
      <RequestProfileTabEmailsThreadModal
        mailUuid={uuid}
        navigationContext={navigationContext}
        requestId={requestData?.id}
        onClose={onClose}
        onReload={handleReloadAttachedThreadChanges}
        onSuccessRead={handleAttachedThreadRead}
      />
    )
  }

  const { handleClickEmailTag } = useTaskTableItemAttachedEmailThreads({
    renderThreadModal,
    onCloseModal: closeModal,
    onSetModal: setModal,
  })

  const { handleClickFileTag } = useTaskTableItemAttachedFiles({
    onAddNotification: handleAddNotification,
    onCloseModal: closeModal,
    onReload: handleReload,
    onSetModal: setModal,
  })

  const { handleOpenEmailPopUp } = useTaskSendEmailTableAction({
    attachProps: getRequestEmailAttachProps({
      requestData,
      requestId: requestData?.id,
    }),
    outboundUuid,
    recipientsFilterModalInitialValues:
      getStandardFilterModalInitialValues(requestData),
    templateListApiParams: getRequestTemplateListApiParams({
      buildingId: requestData?.building?.id,
    }),
    withServiceAccount: true,
    onCloseEmailFormModal: closeEmailFormModal,
    onCloseModal: closeModal,
    onReload: handleReload,
    onSetEmailFormModal: setEmailFormModal,
    onSetModal: setModal,
  })

  const { activeItemIdRef, getFileAttachInputProps, handleOpenFileAttach } =
    useTaskTableItemAttachFile({
      setModal,
      setPerformingMassAction: EMPTY_FUNCTION,
      onAddErrorNotification: handleAddErrorNotification,
      onAddSuccessNotification: handleAddSuccessNotification,
      onCloseModal: closeModal,
      onReload: handleReloadAll,
    })

  function submitActivityFilesFromStorage({ item, selectedFiles }) {
    return getPipelineActivity(item.id)
      .then(({ data }) =>
        updatePipelineActivity(item.id, {
          files: getTaskNextFileIds(data.files, selectedFiles),
        })
      )
      .then(response => {
        handleAddSuccessNotification({
          title: t('Notification.CommonTitleAttachedFilesKey', {
            count: selectedFiles.length,
            totalCount: selectedFiles.length,
          }),
        })
        handleReloadAfterActivityChange(response?.data || item, item?.phase?.id)
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  function handleAttachActivityFilesFromStorage({ directoryId, item }) {
    setModal(
      <FileAttachItemsModal
        ButtonRemove={false}
        directoryId={directoryId}
        getData={getFileItems}
        onClose={closeModal}
        onSubmit={selectedFiles =>
          selectedFiles?.length &&
          submitActivityFilesFromStorage({ item, selectedFiles })
        }
      />
    )
  }

  function handleEditActivitySuccess(task, item) {
    closeModal()
    handleAddNotification(getActivityEditSuccessNotificationProps(t))
    handleReloadAfterStatusChange(item, task || item)
  }

  function handleEditActivity(item) {
    setModal(
      <TaskFormModal
        fieldsProps={{
          hideServiceRequest: true,
          submitButtonText: 'Task.FormEditSubmitBtnKey',
        }}
        getPayload={getTaskFormEditPayload}
        initialValues={getTaskFormInitialValues(item, timezone)}
        title='Modal.EditTaskTitleKey'
        onClose={closeModal}
        onSubmit={updateTask}
        onSuccess={task => handleEditActivitySuccess(task, item)}
      />
    )
  }

  function setActivityItemAction(action, item) {
    switch (action) {
      case ACTIVITY_ACTION_EDIT:
        return handleEditActivity(item)

      case TASK_ACTION_MARK_AS_DONE:
        return handleChangeStatus({
          activity: item,
          data: { done: true },
          id: item.id,
        })

      case TASK_ACTION_MARK_AS_OPEN:
        return handleChangeStatus({
          activity: item,
          data: { done: false },
          id: item.id,
        })

      case TASK_ACTION_MARK_AS_SHARED:
        return handleChangeVisibility({
          activity: item,
          data: { visible_to_tenant: true },
          id: item.id,
        })

      case TASK_ACTION_MARK_AS_HIDDEN:
        return handleChangeVisibility({
          activity: item,
          data: { visible_to_tenant: false },
          id: item.id,
        })

      case TASK_ACTION_ATTACH_EMAILS:
        return handleAttachEmails(item.request?.id, item.id)

      case TASK_ACTION_SEND_TO_PROVIDER:
        return handleOpenEmailPopUp(item, TASK_REQUEST_FIELDS.contractors)

      case TASK_ACTION_SEND_TO_DWELLER:
        return handleOpenEmailPopUp(item, TASK_REQUEST_FIELDS.owner)

      case TASK_ACTION_ATTACH_FILE: {
        activeItemIdRef.current = item.id

        return handleOpenFileAttach()
      }

      case ACTIVITY_ACTION_ATTACH_FROM_DOCUMENT_STORAGE:
        return handleAttachActivityFilesFromStorage({
          directoryId: ukDocumentsFolderId,
          item,
        })

      case ACTIVITY_ACTION_ATTACH_FROM_REQUEST_FOLDER:
        return handleAttachActivityFilesFromStorage({
          directoryId: requestData?.directory_id,
          item,
        })

      default:
        return null
    }
  }

  return {
    getFileAttachInputProps,
    handleChangeStatus,
    handleChangeVisibility,
    handleClickEmailTag,
    handleClickFileTag,
    handleEditActivity,
    setActivityItemAction,
  }
}
